import React, { useEffect, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PlusCircleIcon, X } from "lucide-react";
import { API_URL } from "../../config";
import toast from "react-hot-toast";

export default function UsuarioFormModal({
  isOpen,
  onClose,
  onSalvar,
  usuarios,
  usuario,
}) {
  const [codigo, setCodigo] = useState("");
  const [nome, setNome] = useState("");
  const [funcao, setFuncao] = useState("");
  const [senha, setSenha] = useState("");

  useEffect(() => {
    if (usuario) {
      setCodigo(usuario.codigo || "");
      setNome(usuario.nome || "");
      setFuncao(usuario.funcao || "");
      setSenha(usuario.senha || "");
    } else if (usuarios && usuarios.length > 0) {
      const codigos = usuarios.map((u) => parseInt(u.codigo)).filter(Boolean);
      const maxCodigo = Math.max(...codigos, 0);
      const proximoCodigo = (maxCodigo + 1).toString().padStart(2, "0");
      setCodigo(proximoCodigo);
      setNome("");
      setFuncao("");
      setSenha("");
    } else {
      setCodigo("01");
    }
  }, [usuarios, isOpen, usuario]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const acao = usuario ? "editar" : "inserir";
      const payload = { acao, codigo, nome, funcao, senha };
      if (usuario?.id) payload.id = usuario.id;

      const response = await fetch(`${API_URL}usuarios.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.erro || "Erro desconhecido");

      toast.success(
        usuario
          ? "Usuário atualizado com sucesso!"
          : "Usuário cadastrado com sucesso!"
      );

      onSalvar();
      onClose();
    } catch (error) {
      console.error("❌ Erro ao salvar usuário:", error);
      toast.error("Erro ao salvar usuário. Tente novamente.");
    }
  };

  return (
    <Transition show={isOpen} appear as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center pt-28 px-4 text-center sm:px-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden bg-white shadow-xl transition-all border border-gray-300 rounded-none">
                
                {/* Header */}
                <div className="bg-[#2e3057] text-white flex items-center justify-between px-5 py-4 border-b border-black">
                  <div className="flex items-center gap-2">
                    <PlusCircleIcon className="w-5 h-5 text-white" />
                    <Dialog.Title className="text-lg font-semibold">
                      {usuario ? "Editar Usuário" : "Cadastro de Usuário"}
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={onClose}
                    className="bg-white text-black p-1 rounded hover:bg-gray-200 transition"
                    title="Fechar"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Formulário */}
                <div className="p-6">
                  <form onSubmit={handleSubmit}>
                    {/* Primeira linha: código, nome, senha */}
                    <div className="grid grid-cols-12 gap-4 mb-5">
                      {/* Código pequeno */}
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                        <input
                          type="text"
                          value={codigo}
                          readOnly
                          className="w-full rounded-md border border-gray-300 bg-gray-100 p-3 text-sm cursor-not-allowed focus:outline-none focus:border-black"
                        />
                      </div>

                      {/* Nome */}
                      <div className="col-span-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                        <input
                          type="text"
                          value={nome}
                          onChange={(e) => setNome(e.target.value)}
                          className="w-full p-3 rounded-md border border-gray-300 focus:outline-none focus:border-black text-sm"
                        />
                      </div>

                      {/* Senha */}
                      <div className="col-span-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                        <input
                          type="password"
                          value={senha}
                          onChange={(e) => setSenha(e.target.value)}
                          className="w-full p-3 rounded-md border border-gray-300 focus:outline-none focus:border-black text-sm"
                        />
                      </div>
                    </div>

                    {/* Segunda linha: função */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Função</label>
                      <input
                        type="text"
                        value={funcao}
                        onChange={(e) => setFuncao(e.target.value)}
                        className="w-full p-3 rounded-md border border-gray-300 focus:outline-none focus:border-black text-sm"
                      />
                    </div>

                    {/* Botões */}
                    <div className="flex flex-col sm:flex-row justify-end gap-3">
                      <button
                        type="button"
                        onClick={onClose}
                        className="w-full sm:w-auto inline-flex justify-center rounded bg-[#e50e04] text-white px-4 py-2 text-sm font-medium hover:bg-red-700 hover:scale-105 transition-transform duration-200"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="w-full sm:w-auto inline-flex justify-center rounded bg-[#10b981] text-white px-4 py-2 text-sm font-medium hover:bg[#10b981] hover:scale-105 transition-transform duration-200"
                      >
                        Salvar
                      </button>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
